<template>
    <div>
        <div class="header-with-tiles">
            <div class="email-page-header">
                <div class="title-arrow">
                    <ds-text-button gray as="a" href="/Dashboard/userDashboard.jsp">
                        <ds-icon name="arrow-left" />
                    </ds-text-button>

                    <div>Complaints</div>
                </div>

                <a class="feedback-link" @click="isFeedbackModalOpen=true">
                    <ds-icon name="message" />
                    <div>
                        Leave feedback
                    </div>
                </a>

                <share-feedback-modal
                    :is-open="isFeedbackModalOpen"
                    :feature="$options.EMAIL_ANALYTICS"
                    :slack-channels="[$options.SLACK_CHANNELS.IN_APP_FEEDBACK, $options.SLACK_CHANNELS.EMAIL_ANALYTICS_FEEDBACK]"
                    @close="isFeedbackModalOpen = false"
                />
            </div>

            <ds-inline-alert
                v-if="showMessage"
                leading-icon
                type="critical"
                class="alert-banner"
            >
                {{ getAlertBannerMessage }}
                <ds-inline-alert-footer-link
                    v-if="showAlertBannerLink"
                    href="https://help.infusionsoft.com/help/contact-support"
                >
                    Contact Support
                </ds-inline-alert-footer-link>
            </ds-inline-alert>

            <div class="complaint-details-page">
                <ds-card class="card">
                    <ds-card-header class="card-title">
                        <div class="email-card-header">
                            Complaint rate
                        </div>

                        <div class="last-30-days">
                            Last 30 days
                        </div>
                    </ds-card-header>

                    <ds-card-body>
                        <progress-bar-pi-chart-card
                            :data-by-type="emailComplaintsByType"
                            :progress-bar-value="complaintRate"
                            :progress-bar-upper-limit="complaintRateUpperLimit"
                            :progress-bar-healthy-limit="complaintRateHealthyLimit"
                            :progress-bar-units="'%'"
                            :legend-hover-label="'complaints'"
                            :link-label="'Recent complaints'"
                            link-href="#complaint-report"
                            :empty-state-message="'No email complaints have been recorded for the current reporting period'"
                        />
                    </ds-card-body>
                </ds-card>

                <ds-card class="card">
                    <ds-card-header class="card-title">
                        <div class="email-card-header">
                            Email frequency
                        </div>

                        <div class="last-30-days">
                            Last 30 days
                        </div>
                    </ds-card-header>

                    <ds-card-body>
                        <email-frequency-description-card
                            :average-email-sent-per-contact="averageEmailSentPerContact"
                            :unique-contacts-emailed="uniqueContactsEmailed"
                            :total-emails-sent="totalEmailsSent"
                        />
                    </ds-card-body>
                </ds-card>

                <description-card
                    :class="getClass()"
                    header-text="What is a complaint?"
                    video-address="https://www.google.com/url?q=https://keap.wistia.com/medias/h4f3zvbxfp&sa=D&source=docs&ust=1701821464750006&usg=AOvVaw3XVoBfHxUUBUInSaiuqQ-s"
                    link-address="https://help.keap.com/help/spam-complaints"
                    type="complaint"
                >
                    <template v-slot:content>
                        <div v-if="isEmailHealthContentUpdateEnabled">
                            <p>
                                A <strong>spam complaint</strong> occurs when a recipient marks your email as unwanted—either by clicking "Report Spam" in their inbox or by selecting a spam-related reason when unsubscribing via Keap’s opt-out link.
                            </p>

                            <p>
                                Keap tracks complaints reported by ISPs (via feedback loops) and internal unsubscribe feedback. Complaints are calculated <strong>per provider</strong> using this formula:
                            </p>

                            <p>
                                <strong>Complaint Rate = (Complaints ÷ Unique Email Addresses Sent) × 100</strong>
                            </p>

                            <p>
                                For example, 10 complaints out of 10,000 unique emails sent to Gmail = 0.1% complaint rate.
                            </p>

                            <p>
                                In email marketing, complaint rates <strong>above 0.1% per provider</strong> are considered high and may affect your sender reputation.
                            </p>

                            <p>
                                The complaint progress bar shows the provider with the highest complaint rate. The chart below lists the complaints and provides a breakdown of the reasons recipients reported your email as unsolicited or abusive.
                            </p>
                        </div>

                        <div v-else>
                            <p>
                                A complaint, or "spam complaint", is an email that a recipient does not want (spam is a generalized term).
                                When it comes to the business of email marketing, a complaint is registered whenever a recipient marks an email as spam.
                                Every email recipient is empowered to report any email to their
                                Internet Service Providers (ISP) as spam. The ISP then informs Keap where the spam
                                complaint came from and who sent it.
                            </p>

                            <p>
                                It is generally accepted in the email marketing industry that spam complaint <strong>rates
                                    over 0.1%</strong> (1 complaint per 1000 unique email addresses sent per provider) <strong>are
                                    excessive</strong>. This metric is tracked per email provider and not as global rate.
                            </p>

                            <p>
                                The Complaint Rate progress bar on the left shows the percentage of complaints from the provider (e.g. gmail)
                                that has received the highest rate of reported complaints. The chart shows the breakdown of complaints by sub-category.
                            </p>
                        </div>
                    </template>
                </description-card>

                <ds-card class="card">
                    <ds-card-header class="card-title">
                        <div class="email-card-header">
                            Take action to reduce complaint rate
                        </div>

                        <div class="last-30-days">
                            Recommended actions
                        </div>
                    </ds-card-header>

                    <ds-card-body>
                        <complaint-take-action-checklist-card
                            :app-edition="appEdition"
                            :is-update-help-article-links-enabled="isUpdateHelpArticleLinksEnabled"
                        />
                    </ds-card-body>
                </ds-card>

                <EmailRehabServiceCard
                    v-if="displayRehabServiceCard"
                    class="card"
                    icon-path="/resources/svg/customer-support.svg"
                    :header-text="$t('email.healthWidget.rehabServiceTile.complaints.headerText')"
                    :body-text="$t('email.healthWidget.rehabServiceTile.complaints.bodyText')"
                    :link-address="linkAddress"
                    :help-button-label="$t('email.healthWidget.rehabServiceTile.complaints.helpButtonLabel')"
                />
            </div>
        </div>

        <div class="complaint-report-container">
            <complaint-report id="complaint-report" :timezone="timezone" />
        </div>
    </div>
</template>

<script>
import DescriptionCard from './shared/DescriptionCard';
import ProgressBarPiChartCard from './ProgressBarPiChartCard';
import EmailFrequencyDescriptionCard from './EmailFrequencyDescriptionCard';
import ComplaintReport from './ComplaintReport';
import ComplaintTakeActionChecklistCard from './ComplaintTakeActionChecklistCard.vue';
import axios from 'axios';
import { complianceThresholds } from '@/utils/email.util';
import ShareFeedbackModal from '@/components/shared/ShareFeedbackModal';
import { EMAIL_ANALYTICS } from '@/components/shared/constants/shareFeedback.constants';
import { SLACK_CHANNELS } from '@/components/util/slack/slack.constants';
import EmailRehabServiceCard from './shared/EmailRehabServiceCard';

export default {
    EMAIL_ANALYTICS,
    SLACK_CHANNELS,
    components: {
        EmailRehabServiceCard,
        ProgressBarPiChartCard,
        DescriptionCard,
        EmailFrequencyDescriptionCard,
        ComplaintReport,
        ComplaintTakeActionChecklistCard,
        ShareFeedbackModal,
    },

    props: {
        appId: String,
        appEdition: String,
        isEmailHealthContentUpdateEnabled: {
            type: Boolean,
            default: false,
        },
        isUpdateHelpArticleLinksEnabled: {
            type: Boolean,
            default: false,
        },
        firstName: String,
        lastName: String,
        timezone: String,
        phoneNumber: String,
        email: String,
        isEmailLockedDown: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: true,
            errorLoading: false,
            complaintRate: 0,
            averageEmailSentPerContact: 0,
            uniqueContactsEmailed: 0,
            totalEmailsSent: 0,
            alertBannerMessage: '',
            emailComplaintsByType: [],
            complaintRateHealthyLimit: null,
            complaintRateUpperLimit: null,
            isFeedbackModalOpen: false,
        };
    },

    created() {
        this.load();
    },

    computed: {
        displayRehabServiceCard() {
            return this.complaintRate > this.complaintRateUpperLimit;
        },

        linkAddress() {
            return `https://keap.com/email-deliverability-consultation?appId=${this.appId}&firstName=${this.firstName}&lastName=${this.lastName}&email=${this.email}&phone=${this.phoneNumber}`;
        },

        error_pulling_data() {
            return this.loading === false
                    && this.errorLoading === true;
        },
        getAlertBannerMessage() {
            if (this.isEmailLockedDown) {
                return 'Due to a compliance issue your ability to send email broadcasts has been disabled. '
                        + 'Contact support for details on how to re-enable broadcast sending.';
            }

            if (this.error_pulling_data) {
                return 'An issue was encountered while retrieving your data. Please try reloading the page.';
            }

            if (this.complaintRate > complianceThresholds.complaintRateHealthyLimit) {
                return 'You are about to exceed the complaint limit. '
                        + 'If you do not take action soon, you may fall out of Keap’s Email Compliance Policy and if so, email sending restrictions may occur.';
            }

            return 'Your complaint rate is good.';
        },

        showAlertBannerLink() {
            return this.isEmailLockedDown;
        },

        showMessage() {
            return this.error_pulling_data
                    || this.isEmailLockedDown;
        },

    },

    methods: {
        getClass() {
            if (this.displayRehabServiceCard) {
                return 'single-line-card';
            }

            return 'card';
        },

        async load() {
            await this.loadMetrics();
        },

        async loadMetrics() {
            try {
                const metricsResponse = await axios.get('/app/metrics/getComplaintsPageMetrics');

                this.complaintRate = metricsResponse.data.emailHealthMetrics.complaintRate;
                this.averageEmailSentPerContact = metricsResponse.data.emailSentCounts.averageMailPerRecipient;
                this.uniqueContactsEmailed = metricsResponse.data.emailSentCounts.uniqueContactsEmailed;
                this.totalEmailsSent = metricsResponse.data.emailSentCounts.totalEmailSent;
                this.emailComplaintsByType = metricsResponse.data.emailComplaintsByTypeList
                    .map((complaint) => {
                        return { type: complaint.complaintType, number: complaint.numberOfComplaints };
                    });


                this.complaintRateHealthyLimit = complianceThresholds.complaintRateHealthyLimit;
                this.complaintRateUpperLimit = complianceThresholds.complaintRateUpperLimit;

                this.loading = false;
            } catch (e) {
                this.loading = false;
                this.errorLoading = true;
            }
        },
    },

};

</script>

<style lang="scss" rel="stylesheet/scss" scoped>
.alert-banner {
    display: flex;
}

.complaint-details-page {
    display: flex;
    flex-wrap: wrap;
}

.email-page-header {
    display: flex;
    align-items: center;
    padding-bottom: 0;
}

.title-arrow {
    font-size: $font-size-jumbo;
    align-items: center;
    flex: 50%;
    display: flex;
}

.complaint-report-container {
    margin: auto;
    width: 90vw;
}

.card {
    margin: $spacing-100;
    width: calc(50% - 1.5em);
}

.single-line-card {
    margin: $spacing-100;
    width: calc(100% - 1.5em);
}

.email-card-header {
    flex: 50%;
    float: left;
    font-size: $font-size-lg;
    padding-top: $spacing-100;
    padding-left: $spacing-200;
}

.header-with-tiles {
    max-width: 72rem;
    margin: auto;
}

.last-30-days {
    font-size: $font-size-xs;
    padding-top: $spacing-100;
    padding-left: $spacing-200;
    text-align: left;
}

.feedback-link {
    display: flex;
    font-size: $font-size-sm;
    align-items: center;
    color: $color-blue-700;
}

.card-title {
    display: flex;
    flex-direction: column;
    border-bottom: solid px-to-rem(1) $color-ink-200;
    padding-bottom: $spacing-100;
}

</style>
